// @ts-strict-ignore
import { useUser } from "@dashboard/auth";
import { PAGINATE_BY } from "@dashboard/config";
import { useSampleRequestUpdateTrackingIdMutation } from "@dashboard/graphql";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { commonMessages } from "@dashboard/intl";
import { ListViews } from "@dashboard/types";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import React, { useState, useCallback } from "react";
import { useIntl } from "react-intl";

import SampleRequestListPage from "../../components/SampleRequestListPage/SampleRequestListPage";
import SampleRequestTrackingDialog, { FormData } from "../../components/SampleRequestTrackingDialog/SampleRequestTrackingDialog";
import {
  sampleRequestListUrl,
  SampleRequestListUrlQueryParams,
  SampleRequestListUrlSortField,
} from "../../urls";
import { DEFAULT_SORT_KEY, getSortQueryVariables } from "./sort";
import { useListSampleRequestsQuery } from "@dashboard/graphql";

interface SampleRequestListProps {
  params: SampleRequestListUrlQueryParams;
}

export const SampleRequestList = ({ params }: SampleRequestListProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.SAMPLE_REQUEST_LIST);
  const { user } = useUser();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSampleRequestId, setSelectedSampleRequestId] = useState<string | null>(null);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [dialogErrors, setDialogErrors] = useState<Array<{ field?: string | null; message?: string | null }>>([]);

  usePaginationReset(sampleRequestListUrl, params, settings?.rowNumber);

  const paginationState = createPaginationState(settings?.rowNumber ?? PAGINATE_BY, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      sort: getSortQueryVariables(params),
      tenantId: user?.tenantId || null,
    }),
    [paginationState, user?.tenantId],
  );

  const { data, refetch } = useListSampleRequestsQuery({
    displayLoader: true,
    variables: queryVariables,
    skip: !user?.tenantId,
  });

  const [updateTrackingId, { loading: updateLoading }] = useSampleRequestUpdateTrackingIdMutation({
    onCompleted: (data) => {
      const result = data?.sampleRequestUpdateTrackingId;
      if (result?.success) {
        notify({
          status: "success",
          text: result?.message || "Tracking ID added successfully",
        });
        setDialogOpen(false);
        setSelectedSampleRequestId(null);
        setTrackingId(null);
        setDialogErrors([]);
        refetch();
      } else {
        notify({
          status: "error",
          text: result?.message || "Failed to add tracking ID",
        });
      }
    },
    onError: (error) => {
      notify({
        status: "error",
        text: error.message || "Failed to add tracking ID",
      });
    },
  });

  const paginationValues = usePaginator({
    pageInfo: data?.sampleRequests?.pageInfo,
    paginationState,
    queryString: params,
  });

  const handleSort = createSortHandler(navigate, sampleRequestListUrl, params);

  const handleSearchChange = (query: string) => {
    navigate(
      sampleRequestListUrl({
        ...params,
        query,
      }),
    );
  };

  const handleTrackingIdClick = useCallback((id: string) => {
    const sampleRequest = mapEdgesToItems(data?.sampleRequests)?.find(req => req.id === id);
    setSelectedSampleRequestId(id);
    setTrackingId(sampleRequest?.trackingId || null);
    setDialogErrors([]);
    setDialogOpen(true);
  }, [data]);

  const handleTrackingIdSubmit = useCallback(
    (formData: FormData) => {
      if (!selectedSampleRequestId) {
        return;
      }

      const trimmed = formData.trackingId?.trim();
      if (!trimmed) {
        const message = intl.formatMessage({
          id: "trackingIdRequired",
          defaultMessage: "Tracking ID is required.",
        });
        setDialogErrors([{ field: "trackingId", message }]);
        return;
      }

      updateTrackingId({
        variables: {
          trackingId: trimmed,
          sampleRequestId: selectedSampleRequestId,
        },
      });
    },
    [selectedSampleRequestId, updateTrackingId, intl],
  );

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedSampleRequestId(null);
    setTrackingId(null);
    setDialogErrors([]);
  }, []);

  const confirmButtonState: ConfirmButtonTransitionState = updateLoading ? "loading" : "default";

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <SampleRequestListPage
        settings={settings}
        disabled={!data}
        sampleRequests={mapEdgesToItems(data?.sampleRequests)}
        sort={getSortParams(params)}
        onUpdateListSettings={updateListSettings}
        onSort={handleSort}
        onSearchChange={handleSearchChange}
        initialSearch={params.query || ""}
        params={params}
        onTrackingIdClick={handleTrackingIdClick}
      />
      <SampleRequestTrackingDialog
        open={dialogOpen}
        trackingId={trackingId}
        onClose={handleDialogClose}
        onConfirm={handleTrackingIdSubmit}
        confirmButtonState={confirmButtonState}
        errors={dialogErrors}
      />
    </PaginatorContext.Provider>
  );
};

export default SampleRequestList;